import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate, useLocation } from 'react-router-dom';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userCredits, setUserCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authView, setAuthView] = useState('login'); // 'login', 'signup', 'forgotPassword'
  const navigate = useNavigate();
  const location = useLocation();
  const isInitialAuth = useRef(true);
  const lastAuthEvent = useRef(null);
  const currentUserRef = useRef(null); // Store current user in a ref to avoid dependency cycles

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error getting session:", error);
          setUser(null);
          currentUserRef.current = null;
        } else {
          const sessionUser = data?.session?.user || null;
          setUser(sessionUser);
          currentUserRef.current = sessionUser;
          if (sessionUser) {
            await fetchUserCredits(sessionUser.id);
          }
        }
      } catch (err) {
        console.error("Unexpected error during session retrieval:", err);
        setUser(null);
        currentUserRef.current = null;
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("Auth state changed:", event);
        
        // Get the new user from the session
        const newUser = session?.user || null;
        const currentTime = new Date().getTime();
        
        // Only update user state if it's actually changed
        const hasUserChanged = JSON.stringify(newUser) !== JSON.stringify(currentUserRef.current);
        if (hasUserChanged) {
          setUser(newUser);
          currentUserRef.current = newUser;
          if (newUser) {
            await fetchUserCredits(newUser.id);
          }
        }
        
        setLoading(false);
        
        // Handle SIGNED_IN events
        if (event === 'SIGNED_IN') {
          // If this is an initial auth state setup, don't redirect
          if (isInitialAuth.current) {
            isInitialAuth.current = false;
            lastAuthEvent.current = { event, time: currentTime };
            return;
          }
          
          // Prevent multiple handlers for the same auth event within a short time window
          // This helps avoid unwanted redirects when returning to the tab
          const lastEvent = lastAuthEvent.current;
          if (lastEvent && lastEvent.event === event && currentTime - lastEvent.time < 5000) {
            console.log("Ignoring duplicate auth event within 5 seconds");
            return;
          }
          
          // If we got here through a visibility change or tab return and user already exists, 
          // don't redirect
          if (document.visibilityState === 'visible' && currentUserRef.current !== null) {
            console.log("Ignoring SIGNED_IN event after visibility change with existing user");
            lastAuthEvent.current = { event, time: currentTime };
            return;
          }
          
          // Only redirect for explicit sign-in actions
          if (location.pathname !== '/dashboard') {
            sessionStorage.setItem('lastPath', location.pathname);
          }
          
          lastAuthEvent.current = { event, time: currentTime };
          navigate('/dashboard');
          closeAuthModal();
        } else if (event === 'SIGNED_OUT') {
          // Redirect to home page on sign out
          lastAuthEvent.current = { event, time: currentTime };
          navigate('/');
          setUser(null);
          setUserCredits(0);
        }
      }
    );

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [navigate, location]); // Removed user from dependency array

  // For debugging, but won't affect navigation
  useEffect(() => {
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      console.log("Current session check:", data?.session ? "Active session" : "No session");
    };
    
    checkSession();
    
    // Check session every minute (for debugging)
    const interval = setInterval(checkSession, 60000);
    return () => clearInterval(interval);
  }, []);

  // Also add visibility change handler in AuthContext to keep track of tab visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log("Tab became visible");
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const openAuthModal = (view = 'login') => {
    setAuthView(view);
    setAuthModalOpen(true);
  };

  const closeAuthModal = () => {
    setAuthModalOpen(false);
  };

  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const signup = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
        },
      });
      
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const loginWithGoogle = async () => {
    try {
      // Get current hostname to determine environment
      const currentOrigin = window.location.origin;
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${currentOrigin}/auth/callback`,
        },
      });
      
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) throw error;
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const fetchUserCredits = async (userId) => {
    if (!userId) return;
    
    try {
      const { data, error } = await supabase
        .from('user_credits')
        .select('credits')
        .eq('user_id', userId)
        .single();
        
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching user credits:', error);
        return;
      }
      
      setUserCredits(data?.credits || 0);
    } catch (error) {
      console.error('Error fetching user credits:', error);
    }
  };

  // Public method to refresh user credits
  const refreshCredits = async () => {
    if (user) {
      await fetchUserCredits(user.id);
    }
  };

  const value = {
    user,
    userCredits,
    loading,
    login,
    logout,
    signup,
    resetPassword,
    authModalOpen,
    authView,
    openAuthModal,
    closeAuthModal,
    setAuthView,
    refreshCredits,
    loginWithGoogle
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}